/**
 * PWA UI string catalog — 日本語。
 *
 * 機翻草稿（draft）：zh-TW 来源言語より対訳；公開前に native 校正を推奨。
 * key 集合は `Record<TranslationKey, string>` により zh-TW と整合。
 */
import type { TranslationKey } from './zh-TW';

export const ja: Record<TranslationKey, string> = {
  // ── common ───────────────────────────────────────────────
  'common.back': '戻る',
  'common.backHome': 'ホームへ戻る',
  'common.loading': '読み込み中…',
  'common.unknownError': '不明なエラー',
  'common.logout': 'ログアウト',
  'common.logoutPending': 'ログアウト中…',

  // ── login ────────────────────────────────────────────────
  'login.brand': 'ヨットチケット',
  'login.title': 'ログイン',
  'login.emailPlaceholder': 'you@example.com',
  'login.email': 'メールアドレス',
  'login.password': 'パスワード',
  'login.submit': 'ログイン',
  'login.submitting': 'ログイン中…',
  'login.err2fa': 'このアカウントは 2FA が必要です。現在の scaffold では TOTP フローは未提供です。',
  'login.errBadCredentials': 'メールアドレスまたはパスワードが正しくありません',
  'login.errGeneric': 'ログインに失敗しました。しばらくしてから再度お試しください',

  // ── home ─────────────────────────────────────────────────
  'home.title': 'ヨット商品',
  'home.navMyOrders': '注文履歴',
  'home.navMyRefunds': '払い戻し履歴',
  'home.navRecommend': 'おすすめ行程',
  'home.myTicketsHeading': 'マイチケット（最新 {count} 件）',
  'home.ticketsCapNote': '先頭 10 件を表示。最大 50 件保持（新しい順・本端末に保存）',
  'home.orderRef': '注文 {id}',
  'home.loadFailed': '商品の読み込みに失敗しました：',
  'home.empty': '現在予約可能な商品はありません。',
  'home.noMatch': '条件に一致する商品がありません。検索語またはカテゴリーを調整してください。',
  'home.searchPlaceholder': '商品名または SKU を検索…',
  'home.allCategories': 'すべてのカテゴリー',
  'home.unpublished': '（未公開）',
  'home.buyNow': '今すぐ購入',
  'home.durationMin': '· {minutes} 分',

  // ── product detail ───────────────────────────────────────
  'product.title': '商品詳細',
  'product.loadFailed': '商品の読み込みに失敗しました：',
  'product.durationLabel': '· 所要時間 {minutes} 分',
  'product.skuLabel': 'SKU：',
  'product.categoryLabel': 'カテゴリー：',
  'product.chooseSchedule': '便を選択',
  'product.unpublished': '未公開',

  // ── buy / schedule ───────────────────────────────────────
  'buy.title': '便を選択',
  'buy.unitPrice': '· 単価 {price}',
  'buy.loadingSchedules': '便を読み込み中…',
  'buy.scheduleLoadFailed': '便の読み込みに失敗しました：',
  'buy.noSchedules': 'この商品に予約可能な便はありません。',
  'buy.until': '〜 {time}',
  'buy.cancelled': '欠航',
  'buy.remaining': '残り {remaining} / {capacity} 席',
  'buy.soldOut': '完売',
  'buy.quantity': '数量',
  'buy.subtotal': '小計：',
  'buy.originalTotal': '通常価格：',
  'buy.promoSaved': '割引：',
  'buy.orderFailed': '注文に失敗しました：',
  'buy.submit': '注文する',
  'buy.submitting': '注文を作成中…',
  'buy.pkgLead': 'このセットには以下の体験が含まれます。それぞれの時間をお選びください',
  'buy.pkgSegment': '{n} 番目',
  'buy.pkgPickEach': 'すべての時間を選択してください',
  'buy.pkgNoSlot': 'この体験に選べる時間がありません',
  'buy.pkgClash': '選択済みの時間と重複',
  'buy.pkgNotSellable': 'このセットは現在ご購入いただけません',
  'buy.pkgLimit': 'このセットは最大 {n} 名まで（各体験の空き状況による）',
  'buy.minutes': '分',

  // ── checkout ─────────────────────────────────────────────
  'checkout.title': 'お支払い',
  'checkout.orderNo': '注文番号：',
  'checkout.demoNote':
    'デモモード — このボタンはバックエンドの mock payment-callback を直接呼び出し、注文を HOLD → PAYMENT_PENDING → PAID へ遷移させ、同一トランザクションでチケットを発行します。',
  'checkout.reqFailed': 'お支払いリクエストに失敗しました：',
  'checkout.retryHint': '。もう一度お試しください。',
  'checkout.pending': '処理中…',
  'checkout.retry': '支払いを再試行',
  'checkout.pay': '支払い成功をシミュレート',
  'checkout.oversoldTitle': '便が完売しました',
  'checkout.oversoldLine1':
    '申し訳ございません。お支払いの瞬間にこの便が他のお客様で満席となり、注文を完了できませんでした。',
  'checkout.oversoldLine2':
    'お支払い金額は元の方法へ自動的に返金されます。返金状況は通知でお知らせしますので、別途払い戻し申請は不要です。',
  'checkout.failedTitle': 'お支払いが完了しませんでした',
  'checkout.failedLine1': 'このお支払いを完了できず、注文は作成されませんでした。',
  'checkout.failedLine2':
    '請求された金額があれば自動的に返金されます。ホームへ戻って再度ご注文ください。',
  'checkout.orderState': '注文 {id} · ステータス：{state}',
  'checkout.backReselect': 'ホームへ戻って選び直す',

  // ── order confirm ────────────────────────────────────────
  'confirm.title': '注文完了',
  'confirm.orderLabel': '注文',
  'confirm.statusInline': 'ステータス：{state}',
  'confirm.statusLabel': 'ステータス',
  'confirm.paidAmountLabel': 'お支払い金額',
  'confirm.paidAmount': 'お支払い金額：',
  'confirm.noTickets':
    'チケット一覧を取得できませんでした。このページに直接アクセスした場合は、メールまたは注文履歴から各チケットをご確認ください。',
  'confirm.issuedHeading': '発行済みチケット（{count} 枚）',
  'confirm.viewQr': 'QR を表示 · {id}',

  // ── ticket QR ────────────────────────────────────────────
  'ticket.title': 'チケット QR コード',
  'ticket.loadFailed': 'チケットの読み込みに失敗しました：',
  'ticket.refundLocked': '払い戻し不可',
  'ticket.refundEligible': '払い戻し可能',
  'ticket.qrNote':
    '現地でこの QR コードをスタッフにご提示ください。暗号署名が含まれており、偽造を防止します。',
  'ticket.applyRefund': '払い戻しを申請',

  // ── refund request ───────────────────────────────────────
  'refundReq.title': '払い戻しの申請',
  'refundReq.backToTicket': 'チケットへ戻る',
  'refundReq.loadFailed': 'チケットの読み込みに失敗しました：',
  'refundReq.ticketLabel': 'チケット：',
  'refundReq.statusInline': 'ステータス：{state}',
  'refundReq.cannotRefundState': 'このチケットはステータス {state} のため払い戻しできません。',
  'refundReq.lockedWindow':
    '払い戻し期間が終了しています（REFUND_LOCKED）。オンラインでの払い戻しはできません。',
  'refundReq.reasonLabel': '払い戻し理由（1〜500 文字）',
  'refundReq.reasonPlaceholder': '払い戻しの理由をご記入ください。確認の参考とさせていただきます。',
  'refundReq.charCount': '{count} / 500',
  'refundReq.submitFailed': '送信に失敗しました：',
  'refundReq.submit': '払い戻し申請を送信',
  'refundReq.submitting': '送信中…',
  'refundReq.footnote':
    '払い戻し金額は元の注文明細に基づきます。送信後は払い戻し手続きが開始され、乗船は直ちにブロックされます。',

  // ── refund detail ────────────────────────────────────────
  'refundDetail.title': '払い戻しの進捗',
  'refundDetail.loadFailed': '払い戻し記録の読み込みに失敗しました：',
  'refundDetail.refundNo': '払い戻し番号：',
  'refundDetail.refundStatus': '払い戻しステータス：',
  'refundDetail.ticketStatus': 'チケットステータス：',
  'refundDetail.refundAmount': '払い戻し金額：',
  'refundDetail.allowancePending': '金額は返金済みです。請求書の値引き処理は進行中です。',
  'refundDetail.manualReview':
    'この払い戻しは手動審査に入りました。24 時間以内に担当者が対応します。',
  'refundDetail.pollingNote': '進捗は自動更新されます（10 秒）。終了状態で更新を停止します。',
  'refundDetail.viewTicket': '対応するチケットを表示',

  // ── my orders ────────────────────────────────────────────
  'myOrders.title': '注文履歴',
  'myOrders.empty': 'まだ注文記録がありません。',
  'myOrders.snapshotNote':
    'ステータス／金額は注文成立時点のスナップショットです。本端末に最大 50 件保存されます。',

  // ── my refunds ───────────────────────────────────────────
  'myRefunds.title': '払い戻し履歴',
  'myRefunds.empty': 'まだ払い戻し申請の記録がありません。',
  'myRefunds.ticketRef': 'チケット {id}',
  'myRefunds.rowLoadFailed': '読み込み失敗',

  // ── order detail（D）──────────────────────────────────────
  'orderDetail.title': '注文詳細',
  'orderDetail.loadFailed': '注文の読み込みに失敗しました：',
  'orderDetail.orderNumber': '注文番号：',
  'orderDetail.statusLabel': '注文ステータス：',
  'orderDetail.channelLabel': 'チャネル：',
  'orderDetail.subtotal': '小計：',
  'orderDetail.total': '合計：',
  'orderDetail.createdAt': '注文日時：',
  'orderDetail.holdExpiresAt': '保留期限：',

  // ── account（F）──────────────────────────────────────────
  'account.title': 'アカウント情報',
  'account.loadFailed': 'アカウント情報の読み込みに失敗しました。',
  'account.displayName': '表示名',
  'account.email': 'メールアドレス',
  'account.userId': 'ユーザー ID',
  'account.permissions': '権限',
  'account.permissionsCount': '{count} 件',
  'account.twoFactor': '二段階認証',
  'account.twoFactorOn': '有効',
  'account.twoFactorOff': '無効',
  'account.readonlyNote':
    'このページは読み取り専用です。アカウント情報の変更はサポートへお問い合わせください。',

  // ── 補充（D/E/F 共用）────────────────────────────────────
  'home.navAccount': 'アカウント',
  'ticket.canBoard': '乗船可能',
  'ticket.cannotBoard': '現在乗船不可',
};
